import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ErrorHandler } from '@angular/core';
import { ServiceWorkerModule } from '@angular/service-worker';

import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';

import { IntlModule } from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/fi/all';
import '@progress/kendo-angular-intl/locales/sv/all';
import '@progress/kendo-angular-intl/locales/en/all';

import { AppRoutingModule } from './app-routing.module';
import { BlahModule } from '@headpower/blah-ng';

import { HpoErrorHandler } from './shared/handlers/hpo-errorhandler.service';
import { AppComponent } from './app.component';
import { AppConfig } from './app.config';

import { AppLandingComponent } from './app-landing.component';
import { OpenTargetComponent } from './open-target.component';
import { PageNotFoundComponent } from './page-not-found.component';
import { NoUsageRightComponent } from './no-usage-right.component';
import { NoAdminRightComponent } from './no-admin-right.component';
import { ForbiddenComponent } from './forbidden.component';

import { environment } from '../environments/environment';

import { HpoLayoutModule, SmoothnessEnforcer, ThemeService } from '@headpower/layout';

import { registerLocaleData } from '@angular/common';
import localeFi from '@angular/common/locales/fi';
import localeSv from '@angular/common/locales/sv';
import localeEn from '@angular/common/locales/en';
import { SharedModule } from './shared/shared.module';

import { MatNativeDateModule } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BlahMatPaginatorIntl } from './providers/blah-mat-paginator-intl';

import { AppOAuthStorage } from '@headpower/angular-oauth2-oidc-extensions';
import { ResizeService } from './services/resize.service';
import { FloatingActionButtonModule, FloatingActionButtonService, SpinnerModule, HpoNumberModule, ContentLayoutService } from '@headpower/components';
import '@headpower/components/dayjs-loader';
import { MessageService } from '@progress/kendo-angular-l10n';
import { LocalizationMessageService } from './services/localization-message-service';

import { AppComponentService } from './services/app-component.service';

import { BlahService } from '@headpower/blah-ng';
// Import the Popup module
import { PopupModule } from '@progress/kendo-angular-popup';
import { ElementRef } from '@angular/core';
import { SinglePopupService } from '@progress/kendo-angular-grid';
import { CoreModule } from './core.module';
import { GanttModule } from '@progress/kendo-angular-gantt';
import { PagerModule } from '@progress/kendo-angular-pager';

import { AngularSplitModule } from 'angular-split';
import { MatButtonModule } from '@angular/material/button';

import { HybridUiMapService } from './hybrid-ui-map.service';
import { MapModule } from './map/map.module';
import { AuthModule } from './auth/auth.module';
import { FloatingChatService } from './services/floating-chat.service';
import { IframeComponent } from './modules/Iframe/Iframe.component';
import { SplitAreaService } from './split-area.service';

export function createOAuthStorage() {
    return new AppOAuthStorage('headpower_web_app', 'iwm');
}

export function initConfig(config: AppConfig) {
    return () => config.load(environment.version);
}

export function initBlahFactory(blahService: BlahService): Function {
    return () => blahService.init({
        appVersion: environment.version,
        resourceNames: [
            'default',
            'layout',
            'error',
            'endCustomerInvoice',
            'task',
            'dashboard',
            'components',
            'worksite',
            'invoicingProposal',
            'invoicingProposalFilter',
            'productExtensions',
            'settings',
            'taskStatuses',
            'plan',
            'filter',
            'tendering',
            'tenderingListFilter',
            'admin',
            'newTaskList',
            'satnet',
            'map',
            'networkComponents',
            'floatingChat'
        ]
    });
}

export function initTheme(themeService: ThemeService): Function {
    return () => themeService.init();
}

registerLocaleData(localeFi, 'fi');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeSv, 'sv');

@NgModule({
    declarations: [
        AppComponent,
        PageNotFoundComponent,
        NoUsageRightComponent,
        NoAdminRightComponent,
        AppLandingComponent,
        ForbiddenComponent,
        OpenTargetComponent,
        IframeComponent
    ],
    imports: [
        BrowserModule,
        CoreModule,
        BrowserAnimationsModule,
        FloatingActionButtonModule,
        SpinnerModule,
        MatMenuModule,
        MatNativeDateModule,
        SharedModule,
        IntlModule,
        HpoLayoutModule,
        AppRoutingModule,
        FormsModule,
        MatFormFieldModule,
        MatButtonModule,
        MatInputModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production
        }),
        OAuthModule.forRoot(),
        BlahModule,
        HpoNumberModule,
        PopupModule,
        AngularSplitModule,
        GanttModule,
        PagerModule,
        MapModule,
        AuthModule
    ],
    providers: [
        AppComponentService,
        { provide: ErrorHandler, useClass: HpoErrorHandler },
        { provide: LOCALE_ID, useValue: 'fi-FI' },
        { provide: OAuthStorage, useFactory: createOAuthStorage },
        { provide: AppOAuthStorage, useFactory: createOAuthStorage },
        { provide: APP_INITIALIZER, useFactory: initConfig, deps: [AppConfig], multi: true },
        { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { autoFocus: false, hasBackdrop: true, maxHeight: '90vh' } },
        {
            // Initialize Blah translation service
            provide: APP_INITIALIZER,
            useFactory: initBlahFactory,
            deps: [BlahService],
            multi: true
        },
        ResizeService,
        HybridUiMapService,
        SplitAreaService,
        ContentLayoutService,
        FloatingActionButtonService,
        [{ provide: MessageService, useClass: LocalizationMessageService }],
        { provide: MatPaginatorIntl, useClass: BlahMatPaginatorIntl },
        [{
            provide: SinglePopupService,
            useFactory: () => {
                // return the container ElementRef, where the Kendo Popup component will be injected
                return { nativeElement: document.body } as ElementRef;
            }
        }],
        SmoothnessEnforcer,
        { provide: APP_INITIALIZER, useFactory: initTheme, deps: [ThemeService], multi: true },
        FloatingChatService
    ],
    bootstrap: [
        AppComponent
    ]
})
export class AppModule { }
